<template>
		<div class="sc-container-section">
				<div class="sc-row" role="header">
						<div class="post-disb-header">
						Post Disbursement Change
						</div>
						<div class="sc-form-buttons" v-if="hasFormData && !isEditMode">
								<button class="sc-button cancel" @click="handleClear">Clear</button>
						</div>
				</div>
				<div class="sc-row">
						<div class="post-disb-fields">
								<div class="form-row">
										<div
												v-for="field in formFieldsRow1"
												:key="`${field.name}-${formKey}`"
												class="form-field"
												:class="field.width"
										>
												<FormField
														:isSubmitting="isSaving"
														:isEditing="true"
														:field="field"
														:mode="mode"
														:showErrors="showValidationErrors"
														v-model="formData[field.name]"
														@update:model-value="handleFieldUpdate(field.name, $event)"
												/>
										</div>
								</div>

								<div class="form-row">
										<div
												v-for="field in formFieldsRow2"
												:key="`${field.name}-${formKey}`"
												class="form-field"
												:class="field.width"
										>
												<FormField
														:isSubmitting="isSaving"
														:isEditing="true"
														:field="field"
														:mode="mode"
														:showErrors="showValidationErrors"
														v-model="formData[field.name]"
														@update:model-value="handleFieldUpdate(field.name, $event)"
														@blur="handleFieldBlur(field.name, $event)"
												/>
										</div>
								</div>
						</div>
				</div>
		</div>    
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch, nextTick } from 'vue';
import moment from 'moment';
import { DisbursementVM, DisbursementStatusIds, PostDisbursementChangeReasonTypes, PostDisbursementChangeModel } from '@/types/disbursements';
import { CertificationVM } from '@/types/certifications';
import { 
	usePostDisbursementChangeForm,
	CreatePostDisbursementChangeRequest 
} from '@/composables/disbursements/usePostDisbursementChangeForm';
import { useForm } from 'vee-validate';
import FormField from '@/components/v2/certifications/inputs/FormField.vue';
import * as DisbHelpers from '@/composables/disbursements/disbursementHelpers';
import { useDisbursementsStore } from '@/store/store/disbursementsStore';

const store = useDisbursementsStore();

const props = defineProps<{
	disbursement: DisbursementVM;
	certification: CertificationVM;
}>();

const emit = defineEmits<{
	(e: 'cancel'): void;
	(e: 'saved'): void;
	(e: 'formChanged', hasData: boolean): void;
}>();

const editingPostDisbChangeId = ref<number | undefined>(undefined);
const mode = ref<'add' | 'edit'>('add');
const isEditMode = ref(false);
const isSaving = ref(false);
const showValidationErrors = ref(false);
const formKey = ref(0);
const isClearing = ref(false);

const disbursementRef = ref(props.disbursement);
const certificationRef = ref(props.certification);

const nextBusinessDay = computed(() => DisbHelpers.formatDateYYYYMMDD(props.certification.nextBusinessDay));
const initialValues = ref<Partial<CreatePostDisbursementChangeRequest>>({});

const formData = ref<Partial<CreatePostDisbursementChangeRequest>>({
	disbursementId: props.disbursement.details.id,
	disbursementAmount: 0,
	disbursementAmountDisplay: '',
	postDisbursementChangeReasonTypeId: undefined,
	settleDate: nextBusinessDay.value,
	effectiveDate: '',
	processingDate: '',
	disbursementMethodTypeId: props.disbursement.details.disbursementMethodTypeId,
});

const { formFields, validationSchema } = usePostDisbursementChangeForm({
	disbursement: disbursementRef,
	certification: certificationRef,
	formData: formData,
	mode: mode,
	isEditMode: isEditMode,
});

const { validate, setValues, setFieldValue } = useForm({
	validationSchema: validationSchema,
	initialValues: {
		disbursementAmount: 0,
		disbursementAmountDisplay: '',
		postDisbursementChangeReasonTypeId: undefined,
		settleDate: nextBusinessDay.value,
		effectiveDate: '',
		processingDate: '',
		disbursementMethodTypeId: props.disbursement.details.disbursementMethodTypeId,
	},
	validateOnMount: false,	
});

const formFieldsRow1 = computed(() => {
	return formFields.value.filter(f => 
		f.name === 'disbursementAmount' || 
		f.name === 'postDisbursementChangeReasonTypeId'
	);
});

const formFieldsRow2 = computed(() => {
	return formFields.value.filter(f => 
		f.name === 'processingDate' || 
		f.name === 'effectiveDate' || 
		f.name === 'settleDate' || 
		f.name === 'disbursementMethodTypeId'
	);
});

const hasFormData = computed(() => {	
	if (isEditMode.value) {
		if (!initialValues.value) return false;
				
		const hasChanges = 
			formData.value.postDisbursementChangeReasonTypeId !== initialValues.value.postDisbursementChangeReasonTypeId ||
			formData.value.processingDate !== initialValues.value.processingDate ||
			formData.value.effectiveDate !== initialValues.value.effectiveDate ||
			formData.value.settleDate !== initialValues.value.settleDate ||
			formData.value.disbursementMethodTypeId !== initialValues.value.disbursementMethodTypeId;
		
		return hasChanges;
	}
		
	const initialMethodTypeId = props.disbursement.details.disbursementMethodTypeId;
	return (
		(formData.value.disbursementAmount !== null && formData.value.disbursementAmount !== 0) ||
		formData.value.postDisbursementChangeReasonTypeId !== undefined ||
		(formData.value.settleDate !== 'mm/dd/yyyy' && formData.value.settleDate !== nextBusinessDay.value) ||
		(formData.value.processingDate !== '' && formData.value.processingDate !== undefined) ||
		(formData.value.effectiveDate !== '' && formData.value.effectiveDate !== undefined) ||        
		(formData.value.disbursementMethodTypeId !== undefined && 
			formData.value.disbursementMethodTypeId !== initialMethodTypeId)
	);
});

watch(hasFormData, (newValue) => {	
	emit('formChanged', newValue);
}, { immediate: true });

function handleFieldUpdate(fieldName: string, value: any) {
	formData.value[fieldName] = value;

	if (fieldName === 'disbursementAmount' && !isClearing.value) {
		store.updateFormAmount(props.disbursement.details.id, value);
  	}

	if (fieldName === 'disbursementAmount' && value > 0 && 
		props.disbursement.details.disbursementStatusTypeId === DisbursementStatusIds.POST_DISBURSEMENT_CANCELLATION
	) {
		
		formData.value.postDisbursementChangeReasonTypeId = PostDisbursementChangeReasonTypes.NOT_PROVIDED;
		setValues({
			...formData.value,
			postDisbursementChangeReasonTypeId: PostDisbursementChangeReasonTypes.NOT_PROVIDED
		});
	}
}

function handleFieldBlur(fieldName: string, event: FocusEvent | Event) {
  const value = formData.value[fieldName];

  if (fieldName === "processingDate" && value) {    
    calculateEffectiveDate(value);    
  }
}

function calculateEffectiveDate(processingDate: string | Date) {	
	if (!processingDate || !props.disbursement.details.disbursementDate) return;	
	
	const daysThreshold = props.certification.postDisbursementEffectiveDateDays || 45;
		
	const disbDateStr = getDateString(props.disbursement.details.disbursementDate);
		
	const procDateStr = typeof processingDate === 'string'
		? processingDate.split('T')[0]
		: processingDate.toISOString().split('T')[0];
	
	
	const diffDays = getDaysDifference(disbDateStr, procDateStr);
	const effectiveDateStr = diffDays <= daysThreshold ? disbDateStr : procDateStr;
	formData.value.effectiveDate = effectiveDateStr;
	setFieldValue('effectiveDate', effectiveDateStr);
}

function getDaysDifference(date1String: string, date2String: string): number {
  return Math.abs(moment(date2String).diff(moment(date1String), 'days'));
}

function getDateString(date: any): string {
  if (!date) return '';

  const m = moment(date, moment.ISO_8601, true);

  if (!m.isValid()) return '';

  return m.format('YYYY-MM-DD');
}

function handleClear() {  
	showValidationErrors.value = false;
	
	isClearing.value = true;
  
	formData.value = {
		disbursementId: props.disbursement.details.id,
		disbursementAmount: 0,
		disbursementAmountDisplay: '',
		postDisbursementChangeReasonTypeId: undefined,
		settleDate: nextBusinessDay.value,  
		effectiveDate: '',
		processingDate: '',
		disbursementMethodTypeId: props.disbursement.details.disbursementMethodTypeId,
	};
	
	setValues({
		disbursementAmount: 0,
		disbursementAmountDisplay: '',
		postDisbursementChangeReasonTypeId: undefined,
		settleDate: nextBusinessDay.value, 
		effectiveDate: '',
		processingDate: '',
		disbursementMethodTypeId: props.disbursement.details.disbursementMethodTypeId,
	});
	
	formKey.value++;  
	nextTick(() => {
		const originalAmount = props.disbursement.details.disbursementAmount || 0;
		store.updateFormAmount(props.disbursement.details.id, originalAmount);
		isClearing.value = false;
	});
	emit('formChanged', false);
}

onMounted(() => {    
	setValues({
		disbursementAmount: formData.value.disbursementAmount,
		disbursementAmountDisplay: formData.value.disbursementAmountDisplay,    
		postDisbursementChangeReasonTypeId: formData.value.postDisbursementChangeReasonTypeId,
		settleDate: formData.value.settleDate,
		effectiveDate: formData.value.effectiveDate,
		processingDate: formData.value.processingDate,
		disbursementMethodTypeId: formData.value.disbursementMethodTypeId,
	});
});

function setFormData(
	data: PostDisbursementChangeModel | CreatePostDisbursementChangeRequest, 
	postDisbChangeAmountLedgerId?: number
) {	
	if (postDisbChangeAmountLedgerId) {				
		isEditMode.value = true;
		editingPostDisbChangeId.value = postDisbChangeAmountLedgerId;
		mode.value = 'edit';
		
		formData.value = {
			disbursementId: props.disbursement.details.id,
			disbursementAmount: 'disbursementAmount' in data 
				? data.disbursementAmount 
				: props.disbursement.details.disbursementAmount,
			disbursementAmountDisplay: 'disbursementAmountDisplay' in data 
				? data.disbursementAmountDisplay 
				: props.disbursement.details.disbursementAmountDisplay,
			postDisbursementChangeReasonTypeId: data.postDisbursementChangeReasonTypeId,
			settleDate: DisbHelpers.formatDateYYYYMMDD(data.settleDate),
			effectiveDate: DisbHelpers.formatDateYYYYMMDD(data.effectiveDate),
			processingDate: DisbHelpers.formatDateYYYYMMDD(data.processingDate),
			disbursementMethodTypeId: data.disbursementMethodTypeId,
		};
				
		initialValues.value = { ...formData.value };
				
		setValues({
			disbursementAmount: formData.value.disbursementAmount,
			disbursementAmountDisplay: formData.value.disbursementAmountDisplay,
			postDisbursementChangeReasonTypeId: data.postDisbursementChangeReasonTypeId,
			settleDate: formData.value.settleDate,
			effectiveDate: formData.value.effectiveDate,
			processingDate: formData.value.processingDate,
			disbursementMethodTypeId: data.disbursementMethodTypeId,
		});
		
	} else {		
		isEditMode.value = false;
		editingPostDisbChangeId.value = undefined;
		mode.value = 'add';
		
		formData.value = {
			disbursementId: props.disbursement.details.id,
			disbursementAmount: 'disbursementAmount' in data ? data.disbursementAmount : 0,
			disbursementAmountDisplay: 'disbursementAmountDisplay' in data ? data.disbursementAmountDisplay : '',
			postDisbursementChangeReasonTypeId: 'postDisbursementChangeReasonTypeId' in data ? data.postDisbursementChangeReasonTypeId : undefined,
			settleDate: 'settleDate' in data ? DisbHelpers.formatDateYYYYMMDD(data.settleDate) : '',
			effectiveDate: 'effectiveDate' in data ? DisbHelpers.formatDateYYYYMMDD(data.effectiveDate) : '',
			processingDate: 'processingDate' in data ? DisbHelpers.formatDateYYYYMMDD(data.processingDate) : '',
			disbursementMethodTypeId: 'disbursementMethodTypeId' in data ? data.disbursementMethodTypeId : props.disbursement.details.disbursementMethodTypeId,
		};
		
		initialValues.value = { ...formData.value };
	}		
	emit('formChanged', hasFormData.value);
}

defineExpose({ 
	validateAndGetData: async () => {		
		showValidationErrors.value = true;
		const { valid } = await validate();
		
		if (!valid) {			
			return { valid: false };
		}
		
		if (!formData.value.disbursementId ||
				(!isEditMode.value && 
					(formData.value.disbursementAmount === null ||
					formData.value.disbursementAmount === undefined)
				) ||
				!formData.value.postDisbursementChangeReasonTypeId ||
				!formData.value.settleDate ||
				!formData.value.effectiveDate ||
				!formData.value.processingDate ||
				!formData.value.disbursementMethodTypeId) {			
			return { valid: false };
		}		
		return { valid: true };
	},
	
	getInitialValues: () => initialValues.value,
	getFormData: () => formData.value,
	getEditingPostDisbChangeId: () => editingPostDisbChangeId.value,
	isInEditMode: () => isEditMode.value,
	
	resetForm: () => {
		handleClear();
	},
	setFormData
});
</script>

<style scoped>
.post-disb-form {
	background: white;
	padding: 20px;
	border-radius: 8px;
	width: 100%;
}

.post-disb-header {
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-bottom: 15px;  
	padding-bottom: 10px;
}

.post-disb-header h3 {
	margin: 0;
	font-family: 'Montserrat', sans-serif;
	font-size: 16px;
	font-weight: 700;
}

.post-disb-fields {
	margin-bottom: 0;
}

.form-row {
	display: grid;
	grid-template-columns: repeat(4, 1fr);
	gap: 15px;
	margin-bottom: 15px;
}

.form-field {
	display: flex;
	flex-direction: column;
}

.form-field.col-3 {
	grid-column: span 1;
}

.form-field.col-9 {
	grid-column: span 2;
}
</style>
<style scoped src="@/assets/css/certifications/certification-form.css"></style>